import { z } from 'zod';
import $ from 'jquery';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import 'bootstrap';
import 'gasparesganga-jquery-loading-overlay';

// Type definitions
type FingerprintResult = {
    visitorId: string;
};

// Search data schema
const searchSchema = z.object({
    siteId: z.number(),
    searchSite: z.string(),
    searchType: z.string(),
    searchFuzzy: z.boolean(),
    emailDisabled: z.boolean(),
    showCampus: z.boolean().default(false),
    csrf: z.string(),
    visitorId: z.string().optional(),
});

type SearchData = z.infer<typeof searchSchema>;

let searchData: SearchData | null = null;

const searchResultSchema = z.array(z.object({
    title: z.string(),
    content: z.string(),
    link: z.url(),
    campus: z.string(),
    page_type: z.string(),
}));

type SearchResult = z.infer<typeof searchResultSchema.element>;

// Extend window and jQuery interfaces
type WindowSearch = {
    paginate: (index: number) => void;
}
declare global {
    // eslint-disable-next-line
    interface Window {
        search: WindowSearch & { [key: string]: unknown };
        result?: SearchResult[];
        pages?: SearchResult[][];
    }
    // eslint-disable-next-line
    interface JQuery {
        popover: (options?: unknown) => JQuery;
        tooltip: (options?: unknown) => JQuery;
        dropdown: (options?: string | unknown) => JQuery;
        LoadingOverlay: (action: string) => JQuery;
    }
}

// Helper function to safely call jQuery plugins that may be loaded from CDN
const safeCallPlugin = <T extends JQuery>(
    element: T,
    pluginName: keyof JQuery,
    action: string | unknown
): boolean => {
    if (typeof element[pluginName] === 'function') {
        (element[pluginName] as (action: string | unknown) => JQuery)(action);
        return true;
    }
    return false;
};

// Initialize search functionality
const initializeSearch = (): void => {
    $('#search_field').focus();

    const searchTerm = $('#search_field').val();
    if (searchTerm) {
        executeSearch();
    }

    searchData = searchSchema.parse(window.search) as SearchData;

    loadFingerprint();
    initializePopover();
    initializeEventHandlers();
};

// Load fingerprint
const loadFingerprint = async (): Promise<void> => {
    if (!searchData) return;

    try {
        const fp = await FingerprintJS.load();
        const result = await fp.get() as FingerprintResult;
        searchData.visitorId = result.visitorId;
    } catch (error) {
        console.error('Failed to load fingerprint:', error);
    }
};

// Initialize popover
const initializePopover = (): void => {
    if (!searchData) return;

    $('.esPopover').popover({
        content: $('.popover-container').html(),
        html: true,
        title: 'Search Options',
        placement: 'bottom',
        container: 'body',
    });

    $('.esPopover').on('inserted.bs.popover', () => {
        updatePopoverContent();
    });
};

// Update popover content
const updatePopoverContent = (): void => {
    if (!searchData) return;

    $('.popover-title').addClass('text-center');
    $('.popover-content')
        .find('.searchInfo')
        .attr({
            'data-toggle': 'tooltip',
            'data-placement': 'bottom',
            title: 'Exact search will return only precise matches, while fuzzy search finds approximate matches with tolerance for variations.',
        });

    if (searchData.searchFuzzy) {
        $('.es-search-type-fuzzy').addClass('active');
        $('.es-search-type-exact').removeClass('active');
    } else {
        $('.es-search-type-exact').addClass('active');
        $('.es-search-type-fuzzy').removeClass('active');
    }
};

// Initialize event handlers
const initializeEventHandlers = (): void => {
    $(document).on('click', '.es-search-type-exact', handleExactSearchClick);
    $(document).on('click', '.es-search-type-fuzzy', handleFuzzySearchClick);

    $(document).tooltip({
        selector: '.searchInfo',
    });

    $('#search_button').on('click', handleSearchButtonClick);
    $('#search_field').on('keypress', handleSearchFieldKeypress);
    $('#search_schools li a').on('click', handleSchoolChange);
    $('.schools-dropdown [data-toggle="dropdown"]').dropdown();
};

// Event handlers
const handleExactSearchClick = (): void => {
    if (!searchData) return;

    searchData.searchFuzzy = false;
    $('.es-search-type-exact').addClass('active');
    $('.es-search-type-fuzzy').removeClass('active');
};

const handleFuzzySearchClick = (): void => {
    if (!searchData) return;

    searchData.searchFuzzy = true;
    $('.es-search-type-fuzzy').addClass('active');
    $('.es-search-type-exact').removeClass('active');
};

const handleSearchButtonClick = (event: JQuery.ClickEvent): void => {
    event.preventDefault();
    executeSearch();
};

const handleSearchFieldKeypress = (event: JQuery.KeyPressEvent): void => {
    if (event.which === 13) {
        event.preventDefault();
        $('#search_button').trigger('click');
    }
};

const handleSchoolChange = function (this: HTMLElement): void {
    if (!searchData) return;
    const changeSchool = $(this).text() as string;
    const ids = $(this).data('value') as string;
    searchData.searchSite = String(ids);
    $('#change_school').text(changeSchool);
};

// Close popover on outside click
const handlePopoverOutsideClick = (event: JQuery.ClickEvent): void => {
    $('.esPopover').each(function () {
        const describedBy = $(this).attr('aria-describedby');
        if (!describedBy) return;

        const isClickInsidePopover = $(event.target).closest('.popover-content').length > 0;
        const isClickOnButton = $(event.target).attr('aria-describedby') === describedBy;
        const isClickOnButtonChild =
            $(event.target).closest(`[aria-describedby="${describedBy}"]`).length > 0;

        if (!isClickInsidePopover && !isClickOnButton && !isClickOnButtonChild) {
            $(`[aria-describedby="${describedBy}"]`).trigger('click');
        }
    });
};
// Setup global handlers
$('html').on('click', handlePopoverOutsideClick);

// Pagination function
const paginate = (index: number): void => {
    const pages = window.pages as SearchResult[][];
    if (!pages) return;

    addResults(pages[index] as SearchResult[]);
    $('li').removeClass('active');
    $(`#page${index}`).addClass('active');
    window.scrollTo(0, 0);
};
// Expose functions to window
window.search.paginate = paginate;

// Execute search
const executeSearch = (): void => {
    if (!searchData) return;

    const resultsElement = $('#results');
    resultsElement.empty();

    // Show loading overlay if available (loaded from CDN)
    safeCallPlugin(resultsElement, 'LoadingOverlay', 'show');

    const searchTerm = $('#search_field').val() as string;
    const requestData = {
        term: searchTerm,
        id: searchData.searchSite,
        fuzzy: searchData.searchFuzzy,
        visitorId: searchData.visitorId,
        csrf: searchData.csrf,
    };

    $('#pages').empty();
    searchData.showCampus = searchData.searchSite.indexOf(',') !== -1;

    $.getJSON(`/includes/actions/search.php`, requestData, handleSearchSuccess).fail(handleSearchError);
};

// Handle search success
const handleSearchSuccess = (result: SearchResult[]): void => {
    const results = searchResultSchema.safeParse(result);
    if (!results.success) {
        console.error('Invalid search result', results.error);
        return;
    }
    const resultsElement = $('#results') as JQuery<HTMLElement>;

    // Hide loading overlay if available (loaded from CDN)
    safeCallPlugin(resultsElement, 'LoadingOverlay', 'hide');

    if (!result || result.length === 0) {
        resultsElement.append('No results have been found.');
        return;
    }

    if (result.length > 10) {
        paginateResults(result);
    } else {
        addResults(result);
    }
};

// Paginate results
const paginateResults = (result: SearchResult[]): void => {
    window.pages = [];
    const pages = window.pages;

    while (result.length >= 10) {
        pages.push(result.splice(0, 10));
    }

    if (result.length > 0) {
        pages.push(result);
    }

    if (pages.length > 1) {
        pages.forEach((_, index) => {
            $('#pages').append(
                `<li onclick="window.search.paginate(${index})" id="page${index}"><a>${index + 1}</a></li>`
            );
        });
    }

    paginate(0);
};

// Handle search error
const handleSearchError = (jqXHR: JQuery.jqXHR): void => {
    const resultsElement = $('#results');

    // Hide loading overlay if available (loaded from CDN)
    safeCallPlugin(resultsElement, 'LoadingOverlay', 'hide');

    if (jqXHR.status === 429) {
        displayRateLimitError(jqXHR);
    } else {
        resultsElement.append('An error occurred while searching. Please try again later.');
    }
};

// Display rate limit error
const displayRateLimitError = (jqXHR: JQuery.jqXHR): void => {
    const clone = $('.rate-limit-container').clone() as JQuery<HTMLElement>;
    clone.show();
    $('#results').append(clone);

    const retryAfter = jqXHR.getResponseHeader('Retry-After');
    if (retryAfter) {
        $('.retry-after-time', clone).text(retryAfter);
    }
};

// Add results to DOM
const addResults = (results: SearchResult[]): void => {
    if (!searchData) return;

    const resultsContainer = $('#results') as JQuery<HTMLElement>;
    resultsContainer.empty();

    if (!Array.isArray(results)) {
        console.error('Results is not an array', results);
        return;
    }
    results.forEach((result: SearchResult) => {
        const resultRow = createResultRow(result);
        resultsContainer.append(resultRow[0]);
    });

    resultsContainer.append('<hr>');
};

// Create result row
const createResultRow = (result: SearchResult): JQuery<HTMLElement> => {
    if (!searchData) {
        return $('<div></div>');
    }

    const title = result.title.replace(/"/g, '');
    let content = result.content.replace(/"/g, '');

    if (searchData.emailDisabled) {
        content = content.replace(/[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/gm, '');
    }

    content = content.length <= 4 ? 'No description' : content;

    if (content.length > 100) {
        content = `${content.substring(0, 100)}...`;
    }

    const link = result.link;
    const campus = result.campus;
    const showLink = link.length > 70 ? `${link.substring(0, 70)}...` : link;

    const resultRow = $('#result-row-template').clone() as JQuery<HTMLElement>;
    resultRow.removeAttr('id');
    resultRow.find('[data-title]').html(title);
    resultRow.find('[data-campus-name]').html(campus);
    resultRow.find('[data-url]').html(showLink);
    resultRow.find('[data-content]').html(content);
    resultRow.find('.result-link').attr('href', link);
    resultRow.find('.result-link').attr('title', link);

    if (searchData.showCampus) {
        resultRow.find('.row.campus').show();
    } else {
        resultRow.find('.row.campus').hide();
    }

    resultRow.show();
    return resultRow;
};

// Initialize on document ready
$(() => {
    initializeSearch();
});
